import React, { FC } from 'react';
import { marketplaceHeaderClientGenericClick } from '@wix/bi-logger-marketplace/v2';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { useBi } from '@components/Hooks/useBi';
import { Platform } from '@wix/ambassador-wixstudio-templates-v1-template/types';
import { Box, TextButton } from '@wix/design-system';
import MarketplaceLogo from '@components/Images/MarketplaceLogo';
import { LOGO_HEIGHT, LOGO_WIDTH } from '@components/Header/utils/constants';
import { enableInternalMarketplaceSpec } from '@utils/constants';
import s from './Header.module.scss';

const Header: FC<{
  platform?: Platform;
  isMobileView: boolean;
  link?: string;
  pageName?: string;
  className?: string;
}> = ({ platform, isMobileView, link, pageName, className }) => {
  const logger = useBi();
  const { experiments } = useExperiments({ readOnly: true });
  const isInternal = experiments.enabled(enableInternalMarketplaceSpec);
  const headerClass = className ? s[className] || className : s.header;

  function renderSvgPerPlatform(
    platform: Platform | undefined,
    isMobileView: boolean,
  ) {
    switch (platform) {
      case Platform.STUDIO:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={
              isMobileView
                ? '121.74'
                : className === 'viewTemplateHeader'
                ? '145'
                : '168'
            }
            height={
              isMobileView
                ? '20.3'
                : className === 'viewTemplateHeader'
                ? '24'
                : '28'
            }
            viewBox="0 0 168 28"
            fill="none"
          >
            <path
              fill="#000"
              d="M85.512 19.854l1.297-1.229a8.112 8.112 0 002.457 1.934c.94.47 2.002.705 3.184.705.88 0 1.661-.136 2.344-.41.682-.272 1.22-.651 1.615-1.137.394-.5.591-1.084.591-1.752 0-.849-.356-1.509-1.069-1.979-.713-.485-1.926-.857-3.64-1.114-1.486-.228-2.676-.524-3.571-.887-.88-.38-1.517-.85-1.911-1.41-.394-.562-.592-1.237-.592-2.025 0-.926.258-1.737.774-2.435.516-.712 1.228-1.266 2.138-1.66.91-.395 1.957-.592 3.14-.592 1.35 0 2.54.266 3.571.796a8.974 8.974 0 012.775 2.207l-1.273 1.206c-.653-.804-1.373-1.441-2.162-1.911-.773-.47-1.744-.705-2.912-.705-1.243 0-2.26.28-3.048.841-.788.546-1.183 1.29-1.183 2.23 0 .728.334 1.296 1.001 1.706.668.41 1.813.735 3.435.978 1.547.228 2.783.546 3.708.955.925.395 1.593.903 2.002 1.525.425.621.637 1.372.637 2.252 0 .97-.273 1.827-.819 2.57-.53.728-1.274 1.305-2.23 1.73-.955.409-2.062.614-3.32.614-1.456 0-2.76-.266-3.913-.797a9.773 9.773 0 01-3.026-2.206zM107.39 22.492h-1.752V7.797h-5.414v-1.57h12.58v1.57h-5.414v14.695zM127.659 6.227v9.85c0 2.139-.577 3.807-1.729 5.005-1.153 1.183-2.745 1.774-4.777 1.774-2.017 0-3.61-.591-4.778-1.774-1.152-1.198-1.728-2.866-1.728-5.005v-9.85h1.751v9.85c0 1.684.417 2.973 1.251 3.868.835.88 2.002 1.32 3.504 1.32 1.516 0 2.692-.44 3.526-1.32.834-.895 1.251-2.184 1.251-3.868v-9.85h1.729zM130.609 22.492V6.227h5.255c1.668 0 3.131.35 4.39 1.047a7.587 7.587 0 012.98 2.889c.713 1.213 1.069 2.608 1.069 4.185 0 1.578-.356 2.98-1.069 4.209a7.587 7.587 0 01-2.98 2.89c-1.259.697-2.722 1.046-4.39 1.046h-5.255zm1.751-1.592h3.504c1.349 0 2.517-.273 3.503-.819a5.592 5.592 0 002.298-2.297c.546-.986.819-2.131.819-3.435 0-1.305-.273-2.442-.819-3.413a5.592 5.592 0 00-2.298-2.297c-.986-.546-2.154-.82-3.503-.82h-3.504V20.9zM148.539 22.492h-1.751V6.227h1.751v16.265zM159.483 22.857c-1.213 0-2.335-.213-3.367-.637a8.456 8.456 0 01-2.684-1.775 8.378 8.378 0 01-1.774-2.707c-.41-1.046-.615-2.176-.615-3.39 0-1.213.205-2.335.615-3.366a8.121 8.121 0 011.774-2.685 8.273 8.273 0 012.684-1.797c1.032-.424 2.154-.637 3.367-.637s2.328.213 3.344.637a7.97 7.97 0 012.685 1.797 7.697 7.697 0 011.774 2.685c.425 1.031.637 2.153.637 3.367 0 1.213-.212 2.343-.637 3.389a7.93 7.93 0 01-1.774 2.707 8.14 8.14 0 01-2.685 1.774c-1.016.425-2.131.637-3.344.637zm0-1.593c1.304 0 2.449-.296 3.435-.887a6.193 6.193 0 002.343-2.457c.561-1.046.842-2.237.842-3.572 0-1.334-.281-2.517-.842-3.548a6.193 6.193 0 00-2.343-2.457c-.986-.592-2.131-.887-3.435-.887-1.289 0-2.434.295-3.435.887a6.193 6.193 0 00-2.343 2.457c-.561 1.031-.842 2.214-.842 3.548 0 1.335.281 2.526.842 3.572a6.193 6.193 0 002.343 2.457c1.001.591 2.146.887 3.435.887z"
            ></path>
            <g fill="#000" clipPath="url(#clip0_1424_276882)">
              <path d="M81.543 5.75h-2.406c-.667 0-1.29.33-1.665.884l-3.217 4.745a.216.216 0 01-.358 0L70.68 6.634a2.011 2.011 0 00-1.666-.884H66.61l5.73 8.454-5.698 8.407h2.405c.667 0 1.291-.33 1.666-.883l3.185-4.699a.216.216 0 01.358 0l3.185 4.7c.374.552.999.882 1.666.882h2.405l-5.699-8.407 5.731-8.454zM61.657 7.475v15.136h1.15c.953 0 1.725-.772 1.725-1.724V5.75h-1.15c-.952 0-1.724.772-1.724 1.725zM59.55 5.75h-1.023a2.412 2.412 0 00-2.352 1.878L53.887 17.69l-2.004-9.393c-.37-1.736-2.152-2.906-3.974-2.403-1.161.32-2.022 1.301-2.273 2.479l-1.983 9.304-2.285-10.049a2.414 2.414 0 00-2.352-1.878h-1.023l3.835 16.86h1.452a2.794 2.794 0 002.733-2.21l2.451-11.504a.312.312 0 01.304-.246c.146 0 .273.103.304.246L51.525 20.4a2.793 2.793 0 002.733 2.21h1.457l3.836-16.86z"></path>
            </g>
            <path
              fill="#000"
              fillRule="evenodd"
              d="M0 .987C0 .442.442 0 .987 0h26.026c.545 0 .987.442.987.987v26.026a.987.987 0 01-.987.987H.987A.987.987 0 010 27.013V.987zm13.998 3.215c-5.412 0-9.8 4.388-9.8 9.8 0 5.412 4.388 9.8 9.8 9.8 5.413 0 9.8-4.388 9.8-9.8 0-5.412-4.387-9.8-9.8-9.8z"
              clipRule="evenodd"
            ></path>
            <defs>
              <clipPath id="clip0_1424_276882">
                <path
                  fill="#fff"
                  d="M0 0H43.549V16.861H0z"
                  transform="translate(37.993 5.75)"
                ></path>
              </clipPath>
            </defs>
          </svg>
        );
      case Platform.WIX:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={
              isMobileView
                ? '36.94'
                : className === 'viewTemplateHeader'
                ? '63'
                : '68'
            }
            height={
              isMobileView
                ? '14.3'
                : className === 'viewTemplateHeader'
                ? '20'
                : '26'
            }
            fill="none"
            viewBox="0 0 68 26"
          >
            <g fill="#000" clipPath="url(#clip0_1424_276783)">
              <path d="M67.155 0h-3.71c-1.029 0-1.99.51-2.569 1.362l-4.96 7.318a.333.333 0 01-.551 0l-4.961-7.318A3.101 3.101 0 0047.835 0h-3.71l8.838 13.036L44.175 26h3.709c1.03 0 1.99-.51 2.569-1.362l4.912-7.245a.333.333 0 01.55 0l4.913 7.245A3.102 3.102 0 0063.397 26h3.71l-8.79-12.964L67.156 0zM36.493 2.66V26h1.773a2.66 2.66 0 002.66-2.66V0h-1.773a2.66 2.66 0 00-2.66 2.66zM33.242 0h-1.577a3.719 3.719 0 00-3.627 2.895l-3.53 15.516-3.09-14.484C20.849 1.25 18.102-.555 15.29.22c-1.79.495-3.117 2.007-3.504 3.823L8.728 18.391 5.205 2.896A3.722 3.722 0 001.577 0H0l5.913 25.999h2.24a4.308 4.308 0 004.214-3.41l3.78-17.738a.481.481 0 01.468-.38c.225 0 .422.16.469.38l3.783 17.74a4.307 4.307 0 004.214 3.408h2.247L33.243 0z"></path>
            </g>
            <defs>
              <clipPath id="clip0_1424_276783">
                <path fill="#fff" d="M0 0H67.155V26H0z"></path>
              </clipPath>
            </defs>
          </svg>
        );
      default:
        return (
          <MarketplaceLogo
            width={isMobileView ? '121.74' : LOGO_WIDTH}
            height={isMobileView ? '20.3' : LOGO_HEIGHT}
          />
        );
    }
  }

  return (
    <header className={`${s.baseHeader} ${headerClass}`}>
      <Box verticalAlign="middle">
        <TextButton
          as="a"
          href={link}
          onClick={() => {
            logger.report(
              marketplaceHeaderClientGenericClick({
                clickType: 'wix marketplace',
                pageName,
              }),
            );
          }}
          disabled={!link}
        >
          {renderSvgPerPlatform(platform, isMobileView)}
        </TextButton>
        {isInternal && <span className={s.internal}>&nbsp;/ INTERNAL</span>}
      </Box>
    </header>
  );
};

export default Header;
